import React, { lazy } from 'react';

import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedInOutlined';
import EditIcon from '@mui/icons-material/EditOutlined';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccountOutlined';

import { CREATE_LOCAL_BENCHMARKS, CREATE_STUDENT_INVENTORY_TESTS } from '@rights';
import Breadcrumb from '@common/components/breadcrumb/breadcrumb';
import {
  AnswersBundle,
  TestCreationBundle,
  TestEditorBundle,
  TestsAvailableBundle
} from '@aware/bundles';
import { V2_ROUTE_PAGES } from '@aware/v2-routes/constants';

import V2SettingsIframe from '@aware/common/v2-settings-iframe';
import ConfigureLockdownBrowser from '@aware/common/v2-settings/configure-lockdown-browser';
import CreateNewTestType from '@aware/common/v2-settings/create-new-test-type';
import CreateNewStudentInventoryType from '@aware/common/v2-settings/create-new-student-inventory-type';
import CreateNewTeam from '@aware/common/v2-settings/create-new-team';
import EditMyTeams from '@aware/common/v2-settings/edit-my-teams';

import StudentInventoryDetail from '@aware/pages/v2-author-assessments/student-inventories-panel/student-inventory-detail';
import NewStudentInventoryAssessment from '@aware/pages/v2-author-assessments/student-inventories-panel/new-student-inventory-assessment';

const AuthorAssessmentsBundle = lazy(
  () =>
    import(
      /* webpackChunkName: "aware-author-assessments" */ '@aware/pages/v2-author-assessments/author-assessments'
    )
);

const TestProctoringBundle = lazy(
  () =>
    import(
      /* webpackChunkName: "aware-proctoring-staff-selection" */ '@aware/pages/tests-available/proctoring-staff-selection/proctoring-staff-selection'
    )
);

const PAReflectionEditor = lazy(
  () =>
    import(
      /* webpackChunkName: "aware-pa-reflection-editor" */ '@aware/pages/v2-post-assessment/pa-reflection-editor/pa-reflection-editor'
    )
);

const ASSESSMENTS_ROUTE_CONFIG = {
  title: 'Assessments',
  settings: [
    {
      title: 'Assessment Settings',
      order: 1,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/configure-test-types`,
          title: 'Configure Test Types',
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&leftSrc=manage/tests/testtypeslist.aspx%3Fembed%3D1"
              title="Configure Test Types"
            />
          ),

          rights: [{ right: [CREATE_LOCAL_BENCHMARKS], districtLevel: true }]
        },

        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/create-new-test-type`,
          title: 'Create New Test Type',
          component: CreateNewTestType,
          rights: [{ right: [CREATE_LOCAL_BENCHMARKS], districtLevel: true }]
        },
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/mass-edit`,
          title: 'Mass Edit Assessments',
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=manage/tests/masstesteditor.aspx"
              title="Mass Edit Assessments"
              onLoadCallback={iframe => {
                const closeButtonEl = iframe.contentDocument
                  ?.getElementById('detailsec1')
                  ?.contentDocument?.getElementById('btnCloseEditor');

                if (!closeButtonEl) return;

                const [searchAssessmentsInputEl] = [
                  ...closeButtonEl.parentNode.children
                ].filter(child => child !== closeButtonEl);

                if (searchAssessmentsInputEl)
                  searchAssessmentsInputEl.style.marginTop = '8px';

                closeButtonEl.remove();
              }}
            />
          ),
          rights: [{ right: [CREATE_LOCAL_BENCHMARKS] }]
        },
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/performance-levels`,
          title: 'Performance Levels',
          component: () => (
            <V2SettingsIframe
              iframeSrc="/aware/?embed=1&detailsSrc=manage/tests/performancelevels.aspx"
              title="Performance Levels"
            />
          ),
          rights: [{ right: [CREATE_LOCAL_BENCHMARKS], districtLevel: true }]
        },
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/configure-lockdown-browser`,
          title: 'Configure LockDown Browser',
          component: ConfigureLockdownBrowser,
          rights: [{ right: [CREATE_LOCAL_BENCHMARKS], districtLevel: true }]
        }
      ]
    },
    {
      title: 'Student Inventory Settings',
      order: 2,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/configure-student-inventory-types`,
          title: 'Configure Student Inventory Types',
          breadcrumb: () => (
            <Breadcrumb
              title="Configure Student Inventory Types"
              to={`${V2_ROUTE_PAGES.assessmentsSettings}/configure-student-inventory-types`}
            />
          ),
          component: lazy(
            () =>
              import(
                /* webpackChunkName: "aware-student-profiles" */ '@aware/common/v2-settings/student-inventory-types/configure-student-inventory-types'
              )
          ),
          rights: [{ right: [CREATE_STUDENT_INVENTORY_TESTS] }]
        },
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/create-new-student-inventory-type`,
          title: 'Create New Student Inventory Type',
          component: CreateNewStudentInventoryType,
          rights: [{ right: [CREATE_STUDENT_INVENTORY_TESTS] }]
        }
      ]
    },
    {
      title: 'Team Settings',
      order: 3,
      paths: [
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/create-new-team`,
          title: 'Create New Team',
          exact: true,
          component: () => <CreateNewTeam basePath={V2_ROUTE_PAGES.assessmentsSettings} />
        },
        {
          path: `${V2_ROUTE_PAGES.assessmentsSettings}/edit-my-teams`,
          title: 'Edit My Teams',
          exact: true,
          component: EditMyTeams
        }
      ]
    }
  ],
  administer: {
    path: V2_ROUTE_PAGES.administerAssessments, // Navigation Link
    icon: <AssignmentTurnedInIcon />,
    title: 'Administer',
    breadcrumb: null,
    order: 1,
    routes: [
      // These matches need to come before other matches so that the correct breadcrumb is rendered
      {
        path: `${V2_ROUTE_PAGES.enterAnswers}/:testId`,
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.enterAnswers}/:testId/:sectionIds`,
        breadcrumb: () => (
          <Breadcrumb
            title="Tests Available"
            to={`${V2_ROUTE_PAGES.administerAssessments}/main`}
          />
        )
      },
      {
        path: `${V2_ROUTE_PAGES.administerAssessments}/answers`,
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.administerAssessments}/proctoring`,
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.administerAssessments}/campus-administration`,
        breadcrumb: () => (
          <Breadcrumb
            to={`${V2_ROUTE_PAGES.administerAssessments}/campus-administration`}
            title="Campus Administration"
          />
        )
      },
      {
        path: `${V2_ROUTE_PAGES.administerAssessments}/proctor-panel`,
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.administerAssessments}/:view?`,
        component: ({ match }) =>
          match.params.view === 'answers' ? null : <TestsAvailableBundle />
      },
      {
        path: `${V2_ROUTE_PAGES.enterAnswers}/:testId/:sectionIds/:view`,
        breadcrumb: null,
        component: AnswersBundle
      }
    ]
  },
  author: {
    path: V2_ROUTE_PAGES.authorAssessments, // Navigation link
    icon: <EditIcon />,
    title: 'Author',
    exact: true,
    breadcrumb: () => <Breadcrumb title="Author" to={V2_ROUTE_PAGES.authorAssessments} />,
    order: 2,
    component: AuthorAssessmentsBundle,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.authorAssessments}/new-student-inventory-assessment`,
        exact: true,
        component: NewStudentInventoryAssessment
      },
      {
        path: `${V2_ROUTE_PAGES.authorAssessments}/:id/reflection/:view?/:action?/:questionId?`,
        component: PAReflectionEditor,
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.authorAssessments}/edit/inventory/:id`,
        component: StudentInventoryDetail
      },

      {
        path: [`${V2_ROUTE_PAGES.authorAssessments}/edit*`, '/test-editor*'], // Since there are subpaths, we don't want to show a breadcrumb for these routes
        breadcrumb: null
      },
      {
        path: `${V2_ROUTE_PAGES.authorAssessments}/:action/:id?`,
        component: ({ match }) => {
          if (
            match.params.id === 'inventory' ||
            match.params.action === 'new-student-inventory-assessment' ||
            match.params.id === 'reflection'
          ) {
            return null;
          }

          return match.params.action === 'edit' ? (
            <TestEditorBundle />
          ) : (
            <TestCreationBundle />
          );
        },
        breadcrumb: null
      }
    ]
  },
  proctor: {
    path: V2_ROUTE_PAGES.proctorAssessments,
    icon: <SupervisorAccountIcon />,
    title: 'Proctor',
    breadcrumb: null,
    exact: true,
    order: 3,
    component: TestProctoringBundle,
    routes: [
      {
        path: `${V2_ROUTE_PAGES.proctorAssessments}/impersonate/proctoring`,
        breadcrumb: null,
        component: () => null
      },
      {
        path: `${V2_ROUTE_PAGES.proctorAssessments}/impersonate/:view?`,
        breadcrumb: () => (
          <Breadcrumb title="Staff Selection" to={V2_ROUTE_PAGES.proctorAssessments} />
        ),
        component: TestsAvailableBundle
      }
    ]
  }
};

export default ASSESSMENTS_ROUTE_CONFIG;
